import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Link } from "react-router-dom";
import { fmtEuro, fmtNum } from "../../lib/api";
import { splitChange, trendTitle } from "../../lib/insights";

function Col({ title, color, connected, data, to, testid, adsLabel, adsValue }) {
  return (
    <div className="rounded-lg border border-zinc-800/70 p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-100"><span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />{title}</span>
      </div>
      {!connected ? (
        <div className="mt-3 flex flex-wrap items-center gap-x-2 text-sm text-slate-400" data-testid={`${testid}-empty`}>
          <span>Nessun account {title} collegato</span><span className="text-slate-600">·</span>
          <Link to={to} className="inline-flex min-h-11 items-center font-medium text-slate-100 hover:underline md:min-h-0">Collega</Link>
        </div>
      ) : (
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div><div className="text-slate-500">Spesa</div><div className="tabular-nums text-lg font-bold text-slate-50">{connected ? fmtEuro(data.spend) : "—"}</div></div>
        <div><div className="text-slate-500">{adsLabel}</div><div className="tabular-nums text-lg font-bold text-slate-50">{connected ? fmtNum(adsValue) : "—"}</div></div>
        <div><div className="text-slate-500">Costo per lead (piattaforma)</div><div className="tabular-nums text-base font-bold text-[#C5A059]">{connected && data.cpl_ads != null ? fmtEuro(data.cpl_ads) : "—"}</div></div>
        <div><div className="text-slate-500">Lead arrivati in GHL</div><div className="tabular-nums text-base font-bold text-slate-200">{fmtNum(data.leads)}</div>
          {connected && data.cpl != null && <div className="text-xs text-slate-500">{fmtEuro(data.cpl)} per lead GHL</div>}</div>
      </div>
      )}
    </div>
  );
}

export default function ChannelsWidget({ data }) {
  if (!data) return null;
  const series = data.daily.map((d) => ({ ...d, label: new Date(d.date).toLocaleDateString("it-IT", { day: "numeric", month: "short" }) }));
  const pct = splitChange(series, "meta_spend");
  const title = trendTitle({ subject: "La spesa pubblicitaria", pct, neutral: "Google vs Meta" });
  return (
    <div className="athanor-card p-6 fade-up" data-testid="channels-card">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-slate-100" data-testid="channels-title">{title}</h2>
          <p className="text-xs text-slate-500">Spesa e lead dichiarati dalle piattaforme vs opportunità arrivate in GHL (per pipeline e periodo selezionati)</p>
        </div>
        <span className="text-xs text-slate-500">
          Totale GHL: <span className="tabular-nums text-slate-300" data-testid="channels-total-leads">{fmtNum(data.total_leads)}</span> ·
          altri canali: <span className="tabular-nums text-slate-300" data-testid="channels-other-leads">{fmtNum(data.altro_leads)}</span>
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Col title="Google Ads" color="#4285F4" connected={data.google.connected} data={data.google} to="/google-ads" testid="channels-google" adsLabel="Conversioni Google" adsValue={data.google.conversions} />
        <Col title="Meta Ads" color="#e5e5ea" connected={data.meta.connected} data={data.meta} to="/meta-ads" testid="channels-meta" adsLabel="Lead dichiarati Meta" adsValue={data.meta.results} />
      </div>
      <div className="mt-5 h-56" data-testid="channels-chart" role="img" aria-label={title}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
            <CartesianGrid stroke="#26262a" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#8e8e93", fontSize: 12 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
            <YAxis yAxisId="l" tick={{ fill: "#8e8e93", fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <YAxis yAxisId="r" orientation="right" tick={{ fill: "#8e8e93", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `€${v}`} />
            <Tooltip contentStyle={{ background: "#1d1d20", border: "1px solid #26262a", borderRadius: 10, fontSize: 13 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="l" dataKey="google_leads" name="Lead Google (GHL)" fill="#4285F4" radius={[2, 2, 0, 0]} maxBarSize={40} />
            <Bar yAxisId="l" dataKey="meta_leads" name="Lead Meta (GHL)" fill="#e5e5ea" radius={[2, 2, 0, 0]} maxBarSize={40} />
            <Line yAxisId="l" type="monotone" dataKey="meta_results" name="Lead dichiarati Meta" stroke="#a1a1a6" dot={false} strokeWidth={1.5} />
            <Line yAxisId="r" type="monotone" dataKey="google_spend" name="Spesa Google" stroke="#4285F4" strokeDasharray="4 3" dot={false} strokeWidth={1.5} />
            <Line yAxisId="r" type="monotone" dataKey="meta_spend" name="Spesa Meta" stroke="#64d2ff" strokeDasharray="4 3" dot={false} strokeWidth={1.5} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
